import { Namespace, useT } from "@racoon/i18n";
import {
  Button,
  FieldRow,
  Input,
  Modal,
  ModalContent,
  PhoneInput,
  SearchableSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@racoon/ui";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useInviteUserMutation } from "~/shared/api/user/user.grpc";
import { ConnectProto } from "@racoon/api";
import { roleKeyToEnum, type RoleKey } from "~/shared/api/user/user.grpc";
import { type RoleOption } from "~/shared/lib/role-options";
import { isValidE164 } from "~/shared/lib/phone";

type SendMethod = "whatsapp" | "email";

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-[#f8fafc] p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-[#94a3b8] mb-3">{label}</div>
      {children}
    </div>
  );
}

export const InviteDialog = ({
  open,
  onOpenChange,
  organizations = [],
  fixedOrganizationId,
  roleOptions,
  title,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Pickable target organizations; ignored when fixedOrganizationId is set. */
  organizations?: ConnectProto.Organization[];
  /** Lock the invite to one organization and hide the org picker. */
  fixedOrganizationId?: string | number | bigint;
  /** Show an employee-role selector; without it the invite defaults to customer. */
  roleOptions?: RoleOption[];
  /** Dialog title override; defaults to the customer-invite title. */
  title?: string;
}) => {
  const t = useT();
  const { toast } = useToast();

  const defaultRole: RoleKey = roleOptions?.[0]?.id ?? "USER_ROLE_RECEIVER_USER";
  const [orgId, setOrgId] = useState("");
  const [role, setRole] = useState<RoleKey>(defaultRole);
  const [sendMethod, setSendMethod] = useState<SendMethod>("whatsapp");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);

  const [generate, { isLoading }] = useInviteUserMutation();

  // Fresh state for every invite session — the dialog stays mounted.
  useEffect(() => {
    if (!open) return;
    setOrgId("");
    setRole(defaultRole);
    setSendMethod("whatsapp");
    setPhone("");
    setEmail("");
    setGeneratedLink("");
  }, [open, defaultRole]);

  const hasRoleSelector = !!roleOptions?.length;
  const effectiveOrgId = fixedOrganizationId != null ? String(fixedOrganizationId) : orgId;
  const orgOptions = organizations.map((o) => ({ id: String(o.id), name: o.name }));

  const sendInvite = async (preferredMethod: ConnectProto.PreferredMethod, recipient: string) => {
    if (!effectiveOrgId) return null;
    const roleValue = roleKeyToEnum(role);
    if (roleValue === undefined) return null;
    try {
      return await generate({
        role: roleValue,
        ownerOrganizationId: BigInt(effectiveOrgId),
        preferredMethod,
        recipient,
        lang: "ru",
      }).unwrap();
    } catch {
      // inviteUser is not on the global error middleware's skip list, so the
      // structured error is already toasted once. Swallowing the rejection
      // only keeps the dialog open.
      return null;
    }
  };

  const handleGenerate = async () => {
    const res = await sendInvite(ConnectProto.PreferredMethod.COPY, "");
    if (res?.registrationToken) {
      setGeneratedLink(`${window.location.origin}/register-invite?token=${res.registrationToken}`);
    }
  };

  const handleSend = async () => {
    const isEmail = sendMethod === "email";
    const recipient = isEmail ? email.trim() : phone;
    if (!recipient) return;
    const res = await sendInvite(
      isEmail ? ConnectProto.PreferredMethod.EMAIL : ConnectProto.PreferredMethod.WHATSAPP,
      recipient,
    );
    if (res) {
      // Show the token that was actually delivered, not an earlier one.
      if (res.registrationToken) {
        setGeneratedLink(
          `${window.location.origin}/register-invite?token=${res.registrationToken}`,
        );
      }
      toast({ title: t(Namespace.Customers, "inviteSentSuccess") });
    }
  };

  const copy = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (!generatedLink) return;
    let ok = false;
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(generatedLink);
        ok = true;
      } else {
        // The textarea must live inside the dialog's focus trap, otherwise Radix
        // keeps focus and the selection — and the copy — silently fails.
        const host = e?.currentTarget?.parentElement ?? document.body;
        const ta = document.createElement("textarea");
        ta.value = generatedLink;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        host.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        ok = document.execCommand("copy");
        host.removeChild(ta);
      }
    } catch {
      ok = false;
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast({ title: t(Namespace.Customers, "inviteCopied") });
    } else {
      toast({ title: t(Namespace.Customers, "inviteError.generic"), variant: "destructive" });
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="custom" className="md:!w-[500px]" showClose={false}>
        <div className="flex items-center justify-between px-4 py-4 shrink-0">
          <span className="text-base font-bold text-[#1d1f23]">
            {title ?? t(Namespace.Customers, "inviteTitle")}
          </span>
          <button
            type="button"
            aria-label={t(Namespace.Common, "close")}
            onClick={() => onOpenChange(false)}
            className="w-[22px] h-[22px] flex items-center justify-center rounded-full bg-[#1d1f23] hover:opacity-80 transition-opacity shrink-0"
          >
            <X className="w-3 h-3 text-white" />
          </button>
        </div>

        <div className="px-3 pb-3 space-y-3">
          <Section label={t(Namespace.Customers, "inviteSection.generation")}>
            <FieldRow
              htmlFor={hasRoleSelector ? "invite-role" : "invite-org"}
              label={
                hasRoleSelector
                  ? t(Namespace.Customers, "inviteRoleLabel")
                  : t(Namespace.Customers, "inviteOrgLabel")
              }
            >
              {hasRoleSelector ? (
                <SearchableSelect
                  id="invite-role"
                  value={role}
                  options={roleOptions ?? []}
                  onChange={(next) => {
                    setRole(next as RoleKey);
                    setGeneratedLink("");
                  }}
                  searchPlaceholder={t(Namespace.Employees, "searchRole")}
                  size="sm"
                />
              ) : (
                <SearchableSelect
                  id="invite-org"
                  value={orgId}
                  options={orgOptions}
                  onChange={(next) => {
                    setOrgId(next);
                    setGeneratedLink("");
                  }}
                  placeholder={t(Namespace.Customers, "inviteOrgPlaceholder")}
                  searchPlaceholder={t(Namespace.Organizations, "searchOrganization")}
                  size="sm"
                />
              )}
            </FieldRow>

            <div className="flex justify-end mt-3">
              <Button
                id="invite-generate-button"
                variant="default"
                size="sm"
                className="h-7"
                onClick={handleGenerate}
                disabled={!effectiveOrgId || isLoading}
              >
                {t(Namespace.Customers, "inviteGenerateLink")}
              </Button>
            </div>
          </Section>

          {generatedLink && (
            <Section label={t(Namespace.Customers, "inviteSection.link")}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#1d1f23] truncate">{generatedLink}</span>
                <button
                  id="invite-copy-link-button"
                  type="button"
                  onClick={copy}
                  className="text-sm text-brand-blue shrink-0 hover:opacity-70 transition-opacity"
                >
                  {copied
                    ? t(Namespace.Customers, "inviteCopied")
                    : t(Namespace.Customers, "inviteCopy")}
                </button>
              </div>
            </Section>
          )}

          <Section label={t(Namespace.Customers, "inviteSection.send")}>
            <div className="space-y-3">
              <FieldRow
                htmlFor="invite-send-method"
                label={t(Namespace.Customers, "inviteSendMethodLabel")}
              >
                <Select value={sendMethod} onValueChange={(v) => setSendMethod(v as SendMethod)}>
                  <SelectTrigger id="invite-send-method" className="h-7">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">
                      {t(Namespace.Customers, "inviteMethod.whatsapp")}
                    </SelectItem>
                    <SelectItem value="email">
                      {t(Namespace.Customers, "inviteMethod.email")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FieldRow>

              {sendMethod === "whatsapp" ? (
                <FieldRow
                  htmlFor="invite-recipient"
                  label={t(Namespace.Customers, "inviteRecipientLabel")}
                >
                  <PhoneInput
                    id="invite-recipient"
                    value={phone}
                    onValueChange={setPhone}
                    defaultCountry="KZ"
                    allowedCountries={["KZ", "RU", "US"]}
                    placeholder={t(Namespace.Customers, "inviteRecipientPlaceholderSms")}
                  />
                </FieldRow>
              ) : (
                <FieldRow htmlFor="invite-email" label={t(Namespace.Customers, "inviteEmailLabel")}>
                  <Input
                    id="invite-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-7"
                    placeholder={t(Namespace.Customers, "inviteRecipientPlaceholderEmail")}
                  />
                </FieldRow>
              )}
            </div>

            <div className="flex justify-end mt-3">
              <Button
                id="invite-send-button"
                variant="default"
                size="sm"
                className="h-7"
                onClick={handleSend}
                disabled={
                  !effectiveOrgId ||
                  isLoading ||
                  (sendMethod === "whatsapp" ? !isValidE164(phone) : !email.trim())
                }
              >
                {t(Namespace.Customers, "inviteSend")}
              </Button>
            </div>
          </Section>
        </div>
      </ModalContent>
    </Modal>
  );
};
